import React, { useState } from 'react'
import apiClient from '../services/api'
import SEO from '../components/SEO'
import { useToast } from '../context/ToastContext'
import LoadingSpinner from '../components/LoadingSpinner'
import useSiteSettings from '../hooks/useSiteSettings'

const ContactPage = () => {
  const [formData, setFormData] = useState({ name: '', email: '', message: '' })
  const [submitted, setSubmitted] = useState(false)
  const [loading, setLoading] = useState(false)
  const [newsletterEmail, setNewsletterEmail] = useState('')
  const [newsletterLoading, setNewsletterLoading] = useState(false)
  const { addToast } = useToast()
  const settings = useSiteSettings()

  const handleChange = (event) => {
    const { name, value } = event.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async (event) => {
    event.preventDefault()
    setLoading(true)
    setSubmitted(false)

    try {
      await apiClient.post('/api/cms/contact-messages', formData)
      setSubmitted(true)
      addToast('Thanks! Your message has been sent.', 'success')
      setFormData({ name: '', email: '', message: '' })
      setTimeout(() => setSubmitted(false), 4000)
    } catch (error) {
      addToast(error.response?.data?.message || 'Unable to send your message right now.', 'danger')
    } finally {
      setLoading(false)
    }
  }

  const handleSubscribe = async (event) => {
    event.preventDefault()
    setNewsletterLoading(true)
    try {
      await apiClient.post('/api/cms/subscribe', { email: newsletterEmail })
      addToast('Subscribed successfully', 'success')
      setNewsletterEmail('')
    } catch (error) {
      addToast(error.response?.data?.message || 'Subscription failed', 'danger')
    } finally {
      setNewsletterLoading(false)
    }
  }

  return (
    <div className="container py-5 page-grid-gap themed-page themed-contact-page">
      <SEO
        title="Contact"
        description={`Contact ${settings.siteName}. Get directions, phone number, email, and send us a message. We're here to help.`}
        keywords="contact clinic, appointment, phone, email, location, medical clinic contact"
        url="https://harmonymedical.com/contact"
      />
      <section className="page-hero themed-page-hero themed-page-hero-compact split-page-hero split-page-hero-contact">
        <div className="split-page-hero-layout">
          <div className="split-page-hero-copy">
            <span className="hero-kicker">We're Here To Help</span>
            <h1 className="mb-3">Contact Us</h1>
            <p className="text-muted mb-0">
              Reach out for appointments, general enquiries, preventive care information, specialist availability, or follow-up support.
              Our team is happy to help you find the right service and guide you toward the next best step.
            </p>
          </div>
          <div className="split-page-hero-visual" aria-hidden="true">
            <div className="split-page-hero-support-card">
              <div className="split-info-band-chip">Support Overview</div>
              <div className="split-page-hero-support-list">
                <div className="split-page-hero-support-item">
                  <strong>Appointments</strong>
                  <span>Booking help, doctor availability, and visit scheduling support.</span>
                </div>
                <div className="split-page-hero-support-item">
                  <strong>Patient Guidance</strong>
                  <span>Questions about services, follow-up visits, and preparation before arrival.</span>
                </div>
                <div className="split-page-hero-support-item">
                  <strong>Response Window</strong>
                  <span>Our team replies during clinic hours and prioritizes active patient enquiries quickly.</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="section-divider-wave align-right"></div>

      <section className="editorial-section editorial-band">
        <div className="editorial-heading-block">
          <span className="editorial-kicker">Reach The Clinic</span>
          <h2 className="editorial-title">Find our contact details and location at a glance.</h2>
          <p className="editorial-lead">
            Everything you need for a visit is here in one place, from direct contact details to opening hours and directions.
          </p>
        </div>
        <div className="row g-4 mb-5">
          <div className="col-md-4">
            <div className="card h-100 shadow-sm border-0 p-4 themed-contact-card">
              <h5>Phone</h5>
              <p><a href={`tel:${String(settings.contactPhone).replace(/[^+\d]/g, '')}`}>{settings.contactPhone}</a></p>
              <h5>Email</h5>
              <p><a href={`mailto:${settings.contactEmail}`}>{settings.contactEmail}</a></p>
              <h5>Address</h5>
              <p>{settings.contactAddress}</p>
              <h5>Clinic Hours</h5>
              <p className="mb-2">{settings.contactHoursWeekdays}</p>
              <p className="mb-2">{settings.contactHoursSaturday}</p>
              <p>{settings.contactHoursSunday}</p>
              <h5>Social</h5>
              <p>
                <a href={settings.facebookUrl} target="_blank" rel="noreferrer" className="me-2">Facebook</a>
                <a href={settings.twitterUrl} target="_blank" rel="noreferrer" className="me-2">Twitter</a>
                <a href={settings.instagramUrl} target="_blank" rel="noreferrer">Instagram</a>
              </p>
            </div>
          </div>
          <div className="col-md-8">
            <div className="ratio ratio-16x9 shadow-sm rounded themed-map-frame">
              <iframe
                title="Clinic Location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3068.6865911749164!2d-89.63851978463303!3d39.78172177944581!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x88752eecfbd4f009%3A0x81c7f4d5d8d0a4e!2sSpringfield%2C%20IL%2C%20USA!5e0!3m2!1sen!2sin!4v1700000000000!5m2!1sen!2sin"
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="editorial-section editorial-section-soft">
        <div className="row g-4 mb-5">
          <div className="col-md-6">
            <div className="card h-100 border-0 shadow-sm p-4 themed-feature-card">
              <h5>How We Can Help</h5>
              <p className="mb-2">Appointment booking and specialist scheduling.</p>
              <p className="mb-2">Questions about services, availability, and clinic timings.</p>
              <p className="mb-2">Guidance for follow-up visits and ongoing care plans.</p>
              <p className="mb-0">General wellness enquiries and preventive care information.</p>
            </div>
          </div>
          <div className="col-md-6">
            <div className="card h-100 border-0 shadow-sm p-4 themed-feature-card">
              <h5>Before You Visit</h5>
              <p className="mb-2">Bring any recent prescriptions, reports, or referral notes.</p>
              <p className="mb-2">Arrive a little early if you are a new patient.</p>
              <p className="mb-2">Keep your phone and email available for appointment updates.</p>
              <p className="mb-0">Use the newsletter section below for clinic updates and health tips.</p>
            </div>
          </div>
        </div>
      </section>

      {submitted && <div className="alert alert-success">Thanks! Your message has been sent.</div>}

      <div className="card border-0 shadow-sm themed-form-shell editorial-band">
        <div className="card-body p-4 p-lg-5">
          <div className="editorial-heading-block">
            <span className="editorial-kicker">Send A Message</span>
            <h2 className="editorial-title">Ask a question or request help from the care team.</h2>
            <p className="editorial-lead">
              Use this form for appointment questions, service enquiries, follow-up support, or any general assistance you need from the clinic.
            </p>
          </div>
          <form onSubmit={handleSubmit} className="row g-3">
            <div className="col-md-6">
              <label htmlFor="name" className="form-label">
                Name
              </label>
              <input
                type="text"
                className="form-control"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </div>
            <div className="col-md-6">
              <label htmlFor="email" className="form-label">
                Email
              </label>
              <input
                type="email"
                className="form-control"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </div>
            <div className="col-12">
              <label htmlFor="message" className="form-label">
                Message
              </label>
              <textarea
                className="form-control"
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows="4"
                required
              />
            </div>
            <div className="col-12">
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? 'Sending...' : 'Send Message'}
              </button>
            </div>
          </form>
        </div>
      </div>
      <div className="mt-4 card p-3 themed-newsletter-card editorial-section-soft">
        <div className="editorial-heading-block mb-3">
          <span className="editorial-kicker">Clinic Updates</span>
          <h2 className="editorial-title">Subscribe to our newsletter</h2>
          <p className="editorial-lead">
            Receive clinic updates, seasonal health guidance, and helpful reminders delivered to your inbox.
          </p>
        </div>
        <form onSubmit={handleSubscribe} className="row g-2">
          <div className="col-md-8">
            <input
              type="email"
              className="form-control"
              placeholder="Your email"
              value={newsletterEmail}
              onChange={(e) => setNewsletterEmail(e.target.value)}
              required
            />
          </div>
          <div className="col-md-4">
            <button type="submit" className="btn btn-success w-100" disabled={newsletterLoading}>
              {newsletterLoading ? 'Subscribing...' : 'Subscribe'}
            </button>
          </div>
        </form>
      </div>
      {loading && <LoadingSpinner size="2rem" />}
    </div>
  )
}

export default ContactPage
